import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp, Info, Zap } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';
import { CAPACITY_TIER_CONFIG } from '../lib/grid-capacity';
import api from '../lib/api';

export interface SubstationProperties {
  id: string;
  name: string | null;
  operator: string | null;
  voltage_kv: number | null;
  lines: number | null;
  capacity_tier: string | null;
  county: string | null;
  state: string | null;
  status: string | null;
  metadata: Record<string, unknown> | null;
}

interface SubstationDetailProps {
  substation: SubstationProperties;
  onClose: () => void;
}

interface QueueData {
  active_projects: number;
  pending_mw: number;
  completed_projects: number;
  completed_mw: number;
}

export const SubstationDetail: React.FC<SubstationDetailProps> = ({ substation, onClose }) => {
  const { theme } = useTheme();
  const [methodExpanded, setMethodExpanded] = useState(false);
  const [sourceExpanded, setSourceExpanded] = useState(false);
  const [queueData, setQueueData] = useState<QueueData | null>(null);
  const s = substation;
  const tier = CAPACITY_TIER_CONFIG[s.capacity_tier as keyof typeof CAPACITY_TIER_CONFIG] || CAPACITY_TIER_CONFIG.low;
  const subType = s.metadata && typeof s.metadata === 'object'
    ? (typeof s.metadata === 'string' ? JSON.parse(s.metadata) : s.metadata).sub_type
    : null;

  // Fetch ERCOT queue congestion for the county — TX only (ERCOT is the TX ISO)
  useEffect(() => {
    if (!s.county || s.state !== 'TX') return;
    const countyTitle = s.county.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
    api.get('/power/queue-congestion', { params: { county: countyTitle, state: s.state } })
      .then(res => setQueueData(res.data))
      .catch(() => {});
  }, [s.county, s.state]);

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div>
          <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>
            {s.name && !s.name.startsWith('UNKNOWN') && !s.name.startsWith('TAP')
              ? s.name
              : (() => {
                  const base = s.voltage_kv ? `${Number(s.voltage_kv).toLocaleString()} kV Substation` : 'Substation';
                  const county = s.county
                    ? s.county.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
                    : null;
                  return county ? `${base} in ${county} County` : base;
                })()}
          </h2>
          <div style={{
            marginTop: '4px', display: 'inline-block',
            padding: '2px 8px', fontSize: '10px', fontWeight: 600,
            background: tier.color + '20', color: tier.color,
            borderRadius: '4px',
          }}>
            <Zap size={9} style={{ marginRight: '3px', verticalAlign: '-1px' }} />
            75 MW: {tier.label}
          </div>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
          color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {/* Key metrics */}
        <div style={{ marginBottom: '16px' }}>
          {s.voltage_kv != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
              <span style={{ fontSize: '11px', color: theme.textSecondary }}>Voltage Class</span>
              <span style={{
                fontFamily: designTokens.typography.fontFamily.mono,
                fontSize: '13px', fontWeight: 600, color: theme.text,
              }}>{s.voltage_kv} kV</span>
            </div>
          )}
          {s.lines != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
              <span style={{ fontSize: '11px', color: theme.textSecondary }}>Transmission Lines</span>
              <span style={{
                fontFamily: designTokens.typography.fontFamily.mono,
                fontSize: '13px', fontWeight: 600, color: theme.text,
              }}>{s.lines}</span>
            </div>
          )}
          {subType && subType !== 'NOT AVAILABLE' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: '11px', color: theme.textSecondary }}>Type</span>
              <span style={{
                fontFamily: designTokens.typography.fontFamily.mono,
                fontSize: '13px', fontWeight: 500, color: theme.text,
              }}>{subType}</span>
            </div>
          )}
        </div>

        {/* Capacity Assessment */}
        <Section title="75 MW Assessment">
          <div style={{
            padding: '10px', borderRadius: '6px',
            background: tier.color + '10', border: `1px solid ${tier.color}30`,
            marginBottom: '8px',
          }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: tier.color, marginBottom: '4px' }}>
              {tier.label}
            </div>
            <div style={{ fontSize: '11px', color: theme.textSecondary, lineHeight: 1.5 }}>
              {tier.description}
            </div>
          </div>
          <div style={{
            padding: '8px 10px', borderRadius: '6px',
            background: '#d9770610', border: `1px solid #d9770630`,
            fontSize: '10px', color: '#d97706', lineHeight: 1.5,
          }}>
            Confirm with {s.state === 'TX' ? 'ERCOT' : 'serving ISO/RTO (PJM, SPP, MISO, etc.)'} screening study before committing. Voltage class is a proxy — actual capacity depends on transformer MVA rating, which is CEII-restricted (FERC Form 715).
          </div>
        </Section>

        {/* ERCOT Queue Congestion */}
        {queueData && (
          <Section title={`ERCOT Queue — ${s.county || 'County'}`}>
            <Field label="Active Projects" value={queueData.active_projects > 0 ? String(queueData.active_projects) : '0'} />
            <Field label="Pending Capacity" value={queueData.pending_mw > 0 ? `${queueData.pending_mw.toLocaleString()} MW` : '0 MW'} />
            <Field label="Completed Projects" value={queueData.completed_projects > 0 ? String(queueData.completed_projects) : '0'} />
            <Field label="Completed Capacity" value={queueData.completed_mw > 0 ? `${queueData.completed_mw.toLocaleString()} MW` : '0 MW'} />
            {queueData.pending_mw > 2000 && (
              <div style={{
                marginTop: '8px', padding: '8px 10px', borderRadius: '6px',
                background: '#dc262610', border: '1px solid #dc262630',
                fontSize: '10px', color: '#dc2626', lineHeight: 1.5,
              }}>
                High queue congestion — {queueData.pending_mw.toLocaleString()} MW pending in this county. Expect extended interconnection timelines.
              </div>
            )}
          </Section>
        )}

        {/* Location */}
        <Section title="Location">
          <Field label="County" value={s.county ? `${s.county} Co.` : null} />
          <Field label="State" value={s.state} />
          {s.operator && <Field label="Operator" value={s.operator} />}
        </Section>

        {/* Methodology — expandable */}
        <div style={{ marginTop: '12px' }}>
          <button
            onClick={() => setMethodExpanded(!methodExpanded)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              padding: '2px 8px', fontSize: '10px', fontWeight: 600,
              background: '#4b556320', color: '#8A9CAA',
              borderRadius: '4px', border: 'none', cursor: 'pointer',
            }}
          >
            <Zap size={10} />
            How We Estimate 75 MW Capacity
            {methodExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
          </button>
          {methodExpanded && (
            <div style={{
              marginTop: '6px', padding: '10px', fontSize: '10px',
              border: `1px solid ${theme.borderLight}`,
              borderRadius: '6px', lineHeight: 1.7, color: theme.textSecondary,
            }}>
              <div style={{ fontWeight: 600, color: theme.text, marginBottom: '6px' }}>
                No free public dataset includes substation transformer capacity (MVA).
              </div>
              <div style={{ marginBottom: '8px' }}>
                FERC Form 715 contains thermal ratings for every transmission facility, but it is classified as Critical Energy Infrastructure Information (CEII) and is not publicly available. The only definitive way to confirm 75 MW availability is an {s.state === 'TX' ? 'ERCOT' : 'ISO/RTO'} screening study or a utility response letter.
              </div>
              <div style={{ fontWeight: 600, color: theme.text, marginBottom: '4px' }}>
                Our proxy uses two signals from HIFLD:
              </div>
              <div style={{ paddingLeft: '8px', borderLeft: `2px solid ${theme.borderLight}`, marginBottom: '8px' }}>
                <div><strong style={{ color: theme.text }}>Voltage class</strong> — higher voltage = larger transformers. 345 kV substations typically have 500–1,000 MVA transformers. 138 kV varies from 50–300 MVA.</div>
                <div style={{ marginTop: '4px' }}><strong style={{ color: theme.text }}>Transmission line count</strong> — more lines = higher throughput and redundancy. A substation with 6+ lines is a regional hub, not a rural tap.</div>
              </div>
              <div style={{ fontWeight: 600, color: theme.text, marginBottom: '4px' }}>
                Classification tiers:
              </div>
              <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
                <tbody>
                  {[
                    { tier: 'High', color: '#16a34a', rule: '345+ kV, 3+ lines' },
                    { tier: 'Likely', color: '#0891b2', rule: '345+ kV (any) or 230+ kV, 2+ lines' },
                    { tier: 'Moderate', color: '#d97706', rule: '138 kV, 4+ lines' },
                    { tier: 'Low', color: '#6A7E8E', rule: '138 kV, 2–3 lines' },
                  ].map(r => (
                    <tr key={r.tier}>
                      <td style={{ padding: '3px 6px', color: r.color, fontWeight: 600 }}>{r.tier}</td>
                      <td style={{ padding: '3px 6px', color: theme.textTertiary }}>{r.rule}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: '8px', color: theme.textTertiary }}>
                Substations below 138 kV or with fewer than 2 lines are excluded — they are distribution-grade and cannot serve 75 MW loads.
              </div>
            </div>
          )}
        </div>

        {/* Data Source — expandable */}
        <div style={{ marginTop: '8px' }}>
          <button
            onClick={() => setSourceExpanded(!sourceExpanded)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              padding: '2px 8px', fontSize: '10px', fontWeight: 600,
              background: '#4b556320', color: '#8A9CAA',
              borderRadius: '4px', border: 'none', cursor: 'pointer',
            }}
          >
            <Info size={10} />
            Data Sources
            {sourceExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
          </button>
          {sourceExpanded && (
            <div style={{
              marginTop: '6px', padding: '8px', fontSize: '10px',
              border: `1px solid ${theme.borderLight}`,
              borderRadius: '6px', lineHeight: 1.6,
            }}>
              <div style={{ color: theme.text, fontWeight: 600, marginBottom: '4px' }}>
                Homeland Infrastructure Foundation-Level Data (HIFLD)
              </div>
              <div style={{ color: theme.textTertiary }}>
                Compiled by Oak Ridge National Laboratory (ORNL). Substations: name, voltage, line count, type. Does NOT include transformer MVA ratings.
              </div>
              {queueData && (
                <div style={{ color: theme.textTertiary, marginTop: '4px' }}>
                  ERCOT Interconnection Queue — Generator Interconnection Status (GIS) report. Shows pending and completed generation projects by point of interconnection.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
